import { useMemo, useState, useCallback } from "react";
import { useGetHistoryDetailsQuery } from "@api/auditLog.api";
import { useConstant } from "@hooks/useConstant";
import { useHistoryDetailsListParams } from "../../useHistoryDetailsListParams";
import { HistoryGroupBy } from "@common/constants/history-source-types";
import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
    HistoryDetailsGroup,
    GroupedHistoryDetailsResponse,
    ResourceType,
} from "@common/types/auditLog";
import {
    HistoryRow,
    getShowRevertedParam,
} from "../../../views/historyViewConfig";
import {
    extractReconciliationMetadata,
    ReconciliationMetadata,
} from "../../../utils/reconciliationMetadata";

export enum ReconciliationActionType {
    STARTED = "started",
    COMPLETED = "completed",
    RECONCILED = "reconciled",
    MATCHED = "matched",
    UNMATCHED = "unmatched",
    ADDED = "added",
    REMOVED = "removed",
}

export interface ReconciliationLogItem {
    id: string;
    date: Date;
    actionType: ReconciliationActionType;
    description: string;
    resourceType: ResourceType;
    resourceId: string;
    resource?: Record<string, unknown>;
    amount?: number | string;
    log?: HistoryDetailsItem;
    count?: number;
}

interface UseReconciliationMatchingDataOptions {
    historyRow: HistoryRow | null;
    enabled: boolean;
}

export type { ReconciliationMetadata };

interface UseReconciliationMatchingDataReturn {
    logs: ReconciliationLogItem[];
    filteredLogs: ReconciliationLogItem[];
    isLoading: boolean;
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    totalPages: number;
    reconciliationMetadata: ReconciliationMetadata | null;
}

const formatCurrency = (value: number): string =>
    value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

function isGroupedResponse(
    response: unknown,
): response is GroupedHistoryDetailsResponse {
    return (
        response !== null &&
        typeof response === "object" &&
        "groups" in response &&
        "details" in response
    );
}

function getActionTypeForGroup(
    group: HistoryDetailsGroup,
): ReconciliationActionType | null {
    if (group.resourceType === ResourceType.TRANSACTION) {
        switch (group.changeType) {
            case AuditLogChangeType.UPDATE:
                return ReconciliationActionType.RECONCILED;
            case AuditLogChangeType.CREATE:
                return ReconciliationActionType.ADDED;
            case AuditLogChangeType.DELETE:
                return ReconciliationActionType.REMOVED;
            default:
                return null;
        }
    }
    return null;
}

function getActionTypeForDetail(
    detail: HistoryDetailsItem,
): ReconciliationActionType | null {
    if (detail.resourceType === ResourceType.BANK_RECONCILIATION) {
        if (detail.changeType === AuditLogChangeType.CREATE) {
            return ReconciliationActionType.STARTED;
        }
        const hasClosingBalance = detail.diff?.some(
            (d: AuditLogDiff) =>
                d.field === "closingBalance" && d.after?.value !== null,
        );
        if (hasClosingBalance) {
            return ReconciliationActionType.COMPLETED;
        }
    }
    return null;
}

function extractMetadataFromDetails(
    details: HistoryDetailsItem[],
): ReconciliationMetadata | null {
    const bankReconciliationDetail = details.find(
        (d) => d.resourceType === ResourceType.BANK_RECONCILIATION,
    );
    if (!bankReconciliationDetail?.resource) {
        return null;
    }

    const resource = bankReconciliationDetail.resource;
    const financialAccount = resource.financialAccount as
        | { name?: string }
        | undefined;

    return {
        accountName: financialAccount?.name ?? null,
        openingDate: (resource.openingDate as string) ?? null,
        closingDate: (resource.closingDate as string) ?? null,
        openingBalance: (resource.openingBalance as number) ?? null,
        closingBalance: (resource.closingBalance as number) ?? null,
    };
}

export function useReconciliationMatchingData({
    historyRow,
    enabled,
}: UseReconciliationMatchingDataOptions): UseReconciliationMatchingDataReturn {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const groupBy = historyRow?.historyItem.groupBy;
    const isSourceGrouped = groupBy === HistoryGroupBy.SOURCE;
    const isDiffValueGrouped = groupBy === HistoryGroupBy.DIFF_VALUE;
    const listParams = useHistoryDetailsListParams(historyRow);
    const fallbackDate = useConstant(() => new Date());

    const { data: historyDetailsResponse, isLoading } =
        useGetHistoryDetailsQuery({
            ...listParams,
            date: historyRow?.date ?? fallbackDate,
            sourceType: historyRow?.sourceType ?? "",
            sourceIds: historyRow?.historyItem.sourceIds,
            actorId: historyRow?.actorId,
            organizationActorId: historyRow?.historyItem.organizationActorId,
            grouped: isSourceGrouped || isDiffValueGrouped,
            showReverted: getShowRevertedParam(historyRow),
            page: currentPage,
            limit: 50,
            options: {
                enabled: enabled && !!historyRow && !!historyRow.sourceType,
            },
        });

    const { logs, reconciliationMetadata, totalPages } = useMemo(() => {
        if (!historyDetailsResponse || !historyRow) {
            return {
                logs: [] as ReconciliationLogItem[],
                reconciliationMetadata: null,
                totalPages: 1,
            };
        }

        if (isGroupedResponse(historyDetailsResponse)) {
            const { groups, details } = historyDetailsResponse;

            const metadata = extractMetadataFromDetails(details);
            const openingBalance = metadata?.openingBalance ?? null;
            const closingBalance = metadata?.closingBalance ?? null;

            const logItems: ReconciliationLogItem[] = [];

            for (const detail of details) {
                const actionType = getActionTypeForDetail(detail);
                if (!actionType) {
                    continue;
                }

                let description = "";
                if (actionType === ReconciliationActionType.STARTED) {
                    description =
                        openingBalance != null
                            ? `Started reconciliation with opening balance: $${formatCurrency(openingBalance)}`
                            : "Started reconciliation";
                } else if (actionType === ReconciliationActionType.COMPLETED) {
                    description =
                        closingBalance != null
                            ? `Completed reconciliation with closing balance: $${formatCurrency(closingBalance)}`
                            : "Completed reconciliation";
                }

                logItems.push({
                    id: detail.id,
                    date: new Date(detail.createdAt),
                    actionType,
                    description,
                    resourceType: detail.resourceType,
                    resourceId: detail.resourceId,
                    resource: detail.resource,
                    log: detail,
                });
            }

            for (const group of groups) {
                const actionType = getActionTypeForGroup(group);
                if (!actionType) {
                    continue;
                }

                let description = "";
                const count = group.count;
                const txWord = count === 1 ? "transaction" : "transactions";

                switch (actionType) {
                    case ReconciliationActionType.RECONCILED:
                        description = `Reconciled ${count} ${txWord}`;
                        break;
                    case ReconciliationActionType.ADDED:
                        description = `Added ${count} ${txWord}`;
                        break;
                    case ReconciliationActionType.REMOVED:
                        description = `Removed ${count} ${txWord}`;
                        break;
                }

                logItems.push({
                    id: `group-${group.resourceType}-${group.changeType}`,
                    date: new Date(group.date),
                    actionType,
                    description,
                    resourceType: group.resourceType,
                    resourceId: group.resourceIds[0] ?? "",
                    count,
                });
            }

            const sortOrder: ReconciliationActionType[] = [
                ReconciliationActionType.STARTED,
                ReconciliationActionType.RECONCILED,
                ReconciliationActionType.MATCHED,
                ReconciliationActionType.ADDED,
                ReconciliationActionType.REMOVED,
                ReconciliationActionType.UNMATCHED,
                ReconciliationActionType.COMPLETED,
            ];

            const sortedLogItems = logItems.toSorted(
                (a, b) =>
                    sortOrder.indexOf(a.actionType) -
                    sortOrder.indexOf(b.actionType),
            );

            return {
                logs: sortedLogItems,
                reconciliationMetadata: metadata,
                totalPages: 1,
            };
        }

        const metadata = extractReconciliationMetadata(
            historyRow?.historyItem?.resources,
        );

        if (isDiffValueGrouped && metadata) {
            const logItems: ReconciliationLogItem[] = [];
            const count = historyRow?.count ?? 0;
            const txWord = count === 1 ? "transaction" : "transactions";

            logItems.push({
                id: `reconciled-${historyRow?.historyItem.groupByDiffValue?.value}`,
                date: historyRow.date,
                actionType: ReconciliationActionType.RECONCILED,
                description: `Reconciled ${count} ${txWord}`,
                resourceType: ResourceType.TRANSACTION,
                resourceId: historyRow?.resourceIds[0] ?? "",
                count,
            });

            return {
                logs: logItems,
                reconciliationMetadata: metadata,
                totalPages: 1,
            };
        }

        const pages =
            historyDetailsResponse && "totalPages" in historyDetailsResponse
                ? (historyDetailsResponse.totalPages ?? 1)
                : 1;

        return {
            logs: [] as ReconciliationLogItem[],
            reconciliationMetadata: metadata,
            totalPages: pages,
        };
    }, [historyDetailsResponse, historyRow, isDiffValueGrouped]);

    const filteredLogs = useMemo<ReconciliationLogItem[]>(() => {
        if (!searchTerm) {
            return logs;
        }

        const searchLower = searchTerm.toLowerCase();
        return logs.filter(
            (item) =>
                item.description.toLowerCase().includes(searchLower) ||
                (item.resource &&
                    Object.values(item.resource).some((value) =>
                        String(value ?? "")
                            .toLowerCase()
                            .includes(searchLower),
                    )),
        );
    }, [logs, searchTerm]);

    const handleSearchChange = useCallback((term: string) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return {
        logs,
        filteredLogs,
        isLoading,
        searchTerm,
        setSearchTerm: handleSearchChange,
        currentPage,
        setCurrentPage,
        totalPages,
        reconciliationMetadata,
    };
}
